import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { getMissingInformation } from './missingInformation';
import { MissingInfoField } from './MissingInfoField';
import { openCustomerInPortal, isEmbeddedInPortal } from '../../utils/portal/openInPortal';
import { Spinner } from '../../ui/Spinner/Spinner';
import { Banner } from '../../ui/Banner/Banner';
import { useSessionStore } from '../../stores/session/sessionStore';
import type { RelanceFlagsDto } from '../../types/customerFollowUp/relance';

interface MissingInformationBannerProps {
    flags: RelanceFlagsDto | null;
    customerName: string;
    customerExternalId: number | null;
    onRetry: () => void;
    isSyncing: boolean;
    // Localized label for what the re-sync is currently doing, shown while isSyncing: the backend
    // "Étape X sur 3 — …" phase, or the Acomba extraction heading before any phase is reported. Null
    // when neither is known yet — the spinner alone stands in rather than a made-up step.
    syncPhaseLabel: string | null;
}

export const MissingInformationBanner: React.FC<MissingInformationBannerProps> = ({ flags, customerName, customerExternalId, onRetry, isSyncing, syncPhaseLabel }) => {
    const { t } = useTranslation();
    // A corrective drilldown is only actionable when ComptaAI is a portal session AND is actually
    // running inside the AcombaGO iframe right now. sessionKind is persisted and can stay 'portal'
    // after reopening standalone, so it alone would render a blue link that silently does nothing;
    // the live isEmbeddedInPortal() check is what guarantees AcombaGO can receive the postMessage.
    const isPortalSession = useSessionStore(state => state.sessionKind === 'portal');
    const canOpenInPortal = isPortalSession && isEmbeddedInPortal();

    const { clientItems, transactionItems } = getMissingInformation(flags);
    if (clientItems.length === 0 && transactionItems.length === 0) return null;

    const titleKey = clientItems.some(i => !i.optional)
        ? TranslationKey.CustomerFollowUp.MissingInfo.Title
        : clientItems.length > 0 && transactionItems.length > 0
            ? TranslationKey.CustomerFollowUp.MissingInfo.TitleBothOptional
            : clientItems.length > 0
                ? TranslationKey.CustomerFollowUp.MissingInfo.TitleOptional
                : TranslationKey.CustomerFollowUp.MissingInfo.TitleTransactions;

    return (
        <Banner
            tone="warning"
            testId="missing-info-banner"
            title={t(titleKey)}
            action={!isSyncing ? {
                label: t(TranslationKey.CustomerFollowUp.MissingInfo.Retry),
                onClick: onRetry,
                testId: 'missing-info-retry-btn',
            } : undefined}
        >
            <p>{t(TranslationKey.CustomerFollowUp.MissingInfo.Intro)}</p>
            <ol data-testid="missing-info-steps" className="mt-xs list-decimal space-y-xs pl-xl">
                {clientItems.length > 0 && (
                    <li>
                        {t(TranslationKey.CustomerFollowUp.MissingInfo.Step1Prefix)}{' '}
                        {canOpenInPortal && customerExternalId != null ? (
                            /* Asks the AcombaGO parent to open the customer record in its own tab system. */
                            <button
                                type="button"
                                data-testid="missing-info-open-record"
                                onClick={() => openCustomerInPortal(customerExternalId)}
                                className="cursor-pointer font-semibold text-accent-300 underline hover:text-accent-400"
                            >
                                {customerName}
                            </button>
                        ) : (
                            <span data-testid="missing-info-customer-name" className="font-semibold">
                                {customerName}
                            </span>
                        )}
                        {'.'}
                    </li>
                )}
                {clientItems.length > 0 && (
                    <li>
                        {t(TranslationKey.CustomerFollowUp.MissingInfo.Step2ClientPrefix)}{' '}
                        {clientItems.map((item, index) => (
                            <React.Fragment key={item.labelKey}>
                                {index > 0 && ' · '}
                                <MissingInfoField item={item} canOpenInPortal={canOpenInPortal} />
                            </React.Fragment>
                        ))}
                    </li>
                )}
                {transactionItems.length > 0 && (
                    <li>
                        {t(clientItems.length > 0
                            ? TranslationKey.CustomerFollowUp.MissingInfo.Step2TransactionsAlsoPrefix
                            : TranslationKey.CustomerFollowUp.MissingInfo.Step2TransactionsPrefix)}{' '}
                        {transactionItems.map((item, index) => (
                            <React.Fragment key={item.labelKey}>
                                {index > 0 && ' · '}
                                <MissingInfoField item={item} canOpenInPortal={canOpenInPortal} />
                            </React.Fragment>
                        ))}
                    </li>
                )}
                <li>{t(TranslationKey.CustomerFollowUp.MissingInfo.Save)}</li>
                <li>
                    {t(TranslationKey.CustomerFollowUp.MissingInfo.Step3, {
                        action: t(TranslationKey.CustomerFollowUp.MissingInfo.Retry),
                    })}
                </li>
            </ol>

            {isSyncing && (
                /* Mirrors the main page's sync feedback: whatever the re-sync is currently doing, next to
                   a spinner while it runs. The page reloads once it resolves. */
                <div data-testid="missing-info-sync-progress" role="status" className="mt-s flex items-center gap-m">
                    <Spinner className="size-text" />
                    {syncPhaseLabel && <p>{syncPhaseLabel}</p>}
                </div>
            )}
        </Banner>
    );
};
